import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, Crown, Zap, Shield, DollarSign, TrendingDown, Gavel, Search, Calculator, Clock, ChevronRight, ArrowRight, Check, Star, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMER_DURATION = 10 * 60; // 10 minutes in seconds

const ToolSalesGate = ({ sessionId, children, toolName, onSkip }) => {
  // Session-specific timer key so each user gets their own fresh timer
  const TIMER_KEY = `rv_tool_offer_start_${sessionId}`;
  const { isDark } = useTheme();
  const [hasAccess, setHasAccess] = useState(null); // null = loading
  const [accessType, setAccessType] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [expired, setExpired] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  // Handle skip - use callback if provided, otherwise use Link
  const handleSkipClick = (e) => {
    if (onSkip) {
      e.preventDefault();
      onSkip();
    }
    // If no onSkip, let the Link navigate normally
  };

  const checkAccess = useCallback(async () => {
    try {
      // Build headers - include auth token if available (for dealer access)
      const headers = {};
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const res = await axios.get(`${API}/tools/access/${sessionId || 'dealer'}`, { headers });
      setHasAccess(res.data.has_access);
      setAccessType(res.data.access_type);
    } catch {
      setHasAccess(false);
    }
  }, [sessionId]);

  useEffect(() => { checkAccess(); }, [checkAccess]);

  // Timer logic
  useEffect(() => {
    if (hasAccess !== false) return;

    let startTime = localStorage.getItem(TIMER_KEY);
    if (!startTime) {
      startTime = Date.now().toString();
      localStorage.setItem(TIMER_KEY, startTime);
    }

    const updateTimer = () => {
      const elapsed = Math.floor((Date.now() - parseInt(startTime)) / 1000);
      const remaining = TIMER_DURATION - elapsed;
      if (remaining <= 0) {
        setTimeLeft(0);
        setExpired(true);
      } else {
        setTimeLeft(remaining);
        setExpired(false);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [hasAccess]);

  const handleBundlePurchase = async () => {
    if (!sessionId) { toast.error('Session required'); return; }
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/tools/create-payment?session_id=${sessionId}`);
      if (res.data.approval_url) {
        window.location.href = res.data.approval_url;
      }
    } catch {
      toast.error('Failed to start payment. Please try again.');
      setPurchasing(false);
    }
  };

  // Loading state
  if (hasAccess === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: isDark ? '#0a0a0f' : '#f8f9fa' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#F2A900' }} />
      </div>
    );
  }

  // Has access — show the tool
  if (hasAccess) return children;

  // No access — show sales gate
  const bg = isDark ? '#0a0a0f' : '#f8f9fa';
  const cardBg = isDark ? '#141420' : '#fff';
  const border = isDark ? '#1e1e30' : '#e5e7eb';
  const textPrimary = isDark ? '#f0f0f5' : '#111827';
  const textSecondary = isDark ? '#8b8ba3' : '#6b7280';
  const accent = '#F2A900';

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const timerUrgent = timeLeft !== null && timeLeft < 120; // Last 2 minutes

  return (
    <div data-testid="tool-sales-gate" style={{ background: bg, minHeight: '100vh', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px 40px' }}>

        {/* Skip Link - Top (Mobile Friendly) */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Link 
            to={`/my-matches/${sessionId}`} 
            onClick={handleSkipClick}
            data-testid="skip-to-matches-top"
            style={{ 
              color: textSecondary, 
              fontSize: 14, 
              textDecoration: 'underline',
              display: 'inline-block',
              padding: '8px 16px'
            }}
          >
            No thanks, show me my matches →
          </Link>
        </div>

        {/* Countdown Timer */}
        {!expired && timeLeft !== null && (
          <div data-testid="countdown-timer" style={{
            background: timerUrgent ? '#ef4444' : (isDark ? '#1a1a2e' : '#fef3cd'),
            border: `2px solid ${timerUrgent ? '#ef4444' : accent}`,
            borderRadius: 12, padding: '12px 16px', marginBottom: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap',
            animation: timerUrgent ? 'pulse 1s infinite' : 'none'
          }}>
            <Clock size={18} style={{ color: timerUrgent ? '#fff' : '#ef4444' }} />
            <span style={{ color: timerUrgent ? '#fff' : textPrimary, fontWeight: 700, fontSize: 14 }}>
              Special pricing expires in
            </span>
            <span style={{
              fontFamily: 'monospace', fontSize: 20, fontWeight: 800,
              color: timerUrgent ? '#fff' : '#ef4444',
              background: timerUrgent ? '#dc262680' : '#ef444415',
              padding: '4px 12px', borderRadius: 8, letterSpacing: '0.05em'
            }}>
              {formatTime(timeLeft)}
            </span>
          </div>
        )}

        {/* Expired State */}
        {expired && (
          <div style={{
            background: isDark ? '#1a1a2e' : '#fef2f2',
            border: '2px solid #ef4444',
            borderRadius: 12, padding: '20px', marginBottom: 24, textAlign: 'center'
          }}>
            <X size={32} style={{ color: '#ef4444', margin: '0 auto 8px' }} />
            <p style={{ color: '#ef4444', fontSize: 18, fontWeight: 700 }}>This offer has expired</p>
            <p style={{ color: textSecondary, fontSize: 14, marginTop: 8 }}>
              But you can still get these tools (and more) with VIP Membership
            </p>
            <Link to={`/my-matches/${sessionId}`}>
              <Button style={{ background: accent, color: '#000', fontWeight: 700, marginTop: 16, border: 'none', padding: '12px 32px' }}>
                <Crown size={16} style={{ marginRight: 8 }} /> Get the Full Toolkit — $59.99
              </Button>
            </Link>
          </div>
        )}

        {!expired && (
          <>
            {/* Hero Section */}
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                borderRadius: 20, background: '#22c55e15', border: '1px solid #22c55e30',
                marginBottom: 14
              }}>
                <Zap size={14} style={{ color: '#22c55e' }} />
                <span style={{ color: '#22c55e', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em' }}>ONE-TIME OFFER</span>
              </div>
              <h1 style={{ color: textPrimary, fontSize: 28, fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 1.15, margin: 0 }}>
                Stop Overpaying for Your RV.
              </h1>
              <p style={{ color: textSecondary, fontSize: 15, marginTop: 10, lineHeight: 1.6, maxWidth: 560, marginLeft: 'auto', marginRight: 'auto' }}>
                The same AI-powered tools that RV dealers use to price their inventory — now in your hands.
              </p>
            </div>

            {/* What You Get - Stack on mobile */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>
              <ToolFeatureCard
                icon={<Search size={22} />} color="#ec4899"
                title="AI Deal Analyzer"
                price="$49.99"
                features={[
                  'Instant STEAL or RIP-OFF verdict',
                  'Market value comparison',
                  '5-year depreciation forecast',
                  'Pros, cons & alternatives',
                  'Suggested counter-offer price'
                ]}
                isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary}
              />
              <ToolFeatureCard
                icon={<Calculator size={22} />} color="#22c55e"
                title="Cost Calculator"
                price="$49.99"
                features={[
                  'True cost of ownership (not just sticker)',
                  'Insurance, fuel, maintenance, storage',
                  'Financing with interest breakdown',
                  'Year-by-year depreciation chart',
                  'Monthly cost reality check'
                ]}
                isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary}
              />
            </div>

            {/* Pricing Card */}
            <div data-testid="bundle-pricing-card" style={{
              background: isDark ? 'linear-gradient(135deg, #1a1a2e, #16213e)' : 'linear-gradient(135deg, #fffbeb, #fef3c7)',
              border: `2px solid ${accent}`,
              borderRadius: 16, padding: '24px 20px', textAlign: 'center', marginBottom: 24,
              position: 'relative', overflow: 'hidden'
            }}>
              <div style={{ position: 'absolute', top: 0, right: 0, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, padding: '4px 12px', borderBottomLeftRadius: 10, letterSpacing: '0.05em' }}>
                SAVE 70%
              </div>
              <p style={{ color: textSecondary, fontSize: 13, fontWeight: 500 }}>All 4 tools + Daily Matches, one price</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 6 }}>
                <span style={{ color: textSecondary, fontSize: 22, fontWeight: 600, textDecoration: 'line-through', opacity: 0.5 }}>$104.95</span>
                <span style={{ color: textPrimary, fontSize: 40, fontWeight: 900, letterSpacing: '-0.04em' }}>$59.99</span>
              </div>
              <p style={{ color: textSecondary, fontSize: 12, marginTop: 4 }}>One-time payment. Lifetime access.</p>

              <Button
                data-testid="buy-bundle-btn"
                onClick={handleBundlePurchase}
                disabled={purchasing}
                style={{
                  background: accent, color: '#000', fontWeight: 800, fontSize: 15,
                  border: 'none', padding: '14px 32px', borderRadius: 12, marginTop: 16,
                  boxShadow: `0 4px 20px ${accent}40`, cursor: 'pointer',
                  transition: 'all 0.2s', width: '100%', maxWidth: 320
                }}
              >
                {purchasing ? (
                  <><Loader2 className="w-5 h-5 animate-spin" style={{ marginRight: 8 }} /> Processing...</>
                ) : (
                  <><Lock size={16} style={{ marginRight: 8 }} /> Unlock the Full Toolkit — $59.99</>
                )}
              </Button>

              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
                <span style={{ color: textSecondary, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Shield size={11} /> Secure Stripe checkout
                </span>
                <span style={{ color: textSecondary, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Check size={11} /> Instant access
                </span>
              </div>
            </div>

            {/* Social Proof */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginBottom: 28, flexWrap: 'wrap' }}>
              {[
                { num: '50,000+', label: 'RV transactions analyzed' },
                { num: '$4,200', label: 'avg. savings per buyer' },
                { num: '94%', label: 'of buyers saved money' },
              ].map((stat, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <p style={{ color: accent, fontSize: 22, fontWeight: 800 }}>{stat.num}</p>
                  <p style={{ color: textSecondary, fontSize: 12 }}>{stat.label}</p>
                </div>
              ))}
            </div>

            {/* VIP Upsell */}
            <div data-testid="vip-upsell-card" style={{
              background: cardBg, borderRadius: 14, border: `1px solid ${border}`,
              padding: 24, position: 'relative', overflow: 'hidden'
            }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, #F2A900, #FFD700, #F2A900)` }} />
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: `${accent}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Crown size={24} style={{ color: accent }} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ color: accent, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 4 }}>THE ULTIMATE DEAL</p>
                  <h3 style={{ color: textPrimary, fontSize: 18, fontWeight: 800, margin: 0 }}>
                    Complete Buyer Toolkit — $59.99
                  </h3>
                  <p style={{ color: textSecondary, fontSize: 13, marginTop: 6, lineHeight: 1.6 }}>
                    Everything included, lifetime access:
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 12 }}>
                    {[
                      { icon: <Search size={13} />, text: 'AI Deal Analyzer' },
                      { icon: <Calculator size={13} />, text: 'Cost Calculator' },
                      { icon: <Star size={13} />, text: 'RV vs RV Comparison' },
                      { icon: <Gavel size={13} />, text: 'AI Negotiation Coach', highlight: true },
                      { icon: <TrendingDown size={13} />, text: 'Daily AI RV Matches', highlight: true },
                    ].map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
                        <Check size={14} style={{ color: item.highlight ? accent : '#22c55e', flexShrink: 0 }} />
                        <span style={{ color: item.highlight ? accent : textSecondary, fontSize: 13, fontWeight: item.highlight ? 600 : 400 }}>
                          {item.text}
                        </span>
                        {item.highlight && <span style={{ fontSize: 9, background: `${accent}20`, color: accent, padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>VIP ONLY</span>}
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 10, background: isDark ? '#1a1a2e' : '#fefce8', border: `1px solid ${accent}30` }}>
                    <p style={{ color: textPrimary, fontSize: 13, fontWeight: 600, margin: 0 }}>
                      The <strong>AI Negotiation Coach</strong> alone is worth $34.99.
                    </p>
                    <p style={{ color: textSecondary, fontSize: 12, marginTop: 4 }}>
                      It generates a word-for-word negotiation script with exact dollar amounts — the kind of advice RV consultants charge $200/hour for. Plus daily AI matches find deals you'd never find on your own.
                    </p>
                  </div>

                  <Link to={`/my-matches/${sessionId}`} style={{ textDecoration: 'none' }}>
                    <Button
                      data-testid="vip-upsell-btn"
                      style={{
                        background: 'linear-gradient(135deg, #F2A900, #FFD700)', color: '#000',
                        fontWeight: 800, border: 'none', padding: '14px 24px', borderRadius: 10,
                        marginTop: 16, fontSize: 14, width: '100%',
                        boxShadow: '0 4px 16px rgba(242,169,0,0.3)'
                      }}
                    >
                      <Crown size={16} style={{ marginRight: 8 }} />
                      Get the Full Toolkit — $59.99 (Best Value)
                      <ArrowRight size={16} style={{ marginLeft: 8 }} />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Skip Link - Bottom (Mobile Friendly) */}
            <div style={{ textAlign: 'center', marginTop: 24, padding: '16px' }}>
              <Link 
                to={`/my-matches/${sessionId}`}
                onClick={handleSkipClick}
                data-testid="skip-to-matches-bottom"
                style={{ 
                  color: textSecondary, 
                  fontSize: 15, 
                  textDecoration: 'underline',
                  display: 'inline-block',
                  padding: '12px 24px',
                  fontWeight: 500
                }}
              >
                No thanks, show me my matches →
              </Link>
            </div>
          </>
        )}
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
      `}</style>
    </div>
  );
};

const ToolFeatureCard = ({ icon, color, title, price, features, isDark, cardBg, border, textPrimary, textSecondary }) => (
  <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</div>
      <div>
        <h3 style={{ color: textPrimary, fontSize: 15, fontWeight: 700, margin: 0 }}>{title}</h3>
        <span style={{ color: textSecondary, fontSize: 13, textDecoration: 'line-through' }}>{price} value</span>
      </div>
    </div>
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {features.map((f, i) => (
        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 0' }}>
          <Check size={14} style={{ color: '#22c55e', flexShrink: 0, marginTop: 2 }} />
          <span style={{ color: textSecondary, fontSize: 13, lineHeight: 1.4 }}>{f}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default ToolSalesGate;
